// Everything that only exists inside the phone app (Capacitor).
//
// In a browser every function here is a harmless no-op, so the website
// is unchanged and the plugins are never loaded: they are imported only
// when the app is actually running on a phone.
//
// The one thing stored on the phone is the DEVICE TOKEN from the server
// (backend/app-devices.js): a way of staying signed in, never of signing
// in. It lives in the iPhone Keychain or Android Keystore, locked to the
// faces or fingerprints enrolled when it was saved. Someone who adds
// their own face to a coach's phone cannot use it; the coach signs in
// with their password once more instead.
import { Capacitor } from "@capacitor/core";

const SERVER = "app.grtp.co.uk";
// One Face ID read on opening, then the rotated token is saved straight
// back. On Android saving also needs biometrics, and this lets the one
// scan cover both, rather than asking twice every time the app opens.
const AUTH_VALIDITY_SECONDS = 15;

export const isNativeApp = () => Capacitor.isNativePlatform();
export const nativePlatform = () => Capacitor.getPlatform(); // "ios" | "android" | "web"

const plugin = () => import("@capgo/capacitor-native-biometric");

/** What to call the phone's biometrics on screen. Null if there are none. */
export async function biometricName() {
  if (!isNativeApp()) return null;
  try {
    const { NativeBiometric, BiometryType } = await plugin();
    const r = await NativeBiometric.isAvailable({ useFallback: false });
    if (!r.isAvailable) return null;
    switch (r.biometryType) {
      case BiometryType.FACE_ID: return "Face ID";
      case BiometryType.TOUCH_ID: return "Touch ID";
      case BiometryType.FACE_AUTHENTICATION: return "face unlock";
      case BiometryType.FINGERPRINT: return "fingerprint";
      default: return nativePlatform() === "ios" ? "Face ID" : "fingerprint or face unlock";
    }
  } catch {
    return null;
  }
}

export async function hasSavedSignIn() {
  if (!isNativeApp()) return false;
  try {
    const { NativeBiometric } = await plugin();
    return (await NativeBiometric.isCredentialsSaved({ server: SERVER })).isSaved === true;
  } catch {
    return false;
  }
}

export async function saveSignIn(deviceId, deviceToken) {
  const { NativeBiometric, AccessControl } = await plugin();
  await NativeBiometric.setCredentials({
    username: deviceId,
    password: deviceToken,
    server: SERVER,
    accessControl: AccessControl.BIOMETRY_CURRENT_SET,
    authValidityDuration: AUTH_VALIDITY_SECONDS,
    title: "Stay signed in to GRTP Tracker",
    negativeButtonText: "Cancel",
  });
}

/** Prompts for Face ID or fingerprint. Throws if cancelled or refused. */
export async function readSignIn() {
  const { NativeBiometric } = await plugin();
  const c = await NativeBiometric.getSecureCredentials({
    server: SERVER,
    reason: "Unlock GRTP Tracker",
    title: "Unlock GRTP Tracker",
    negativeButtonText: "Use password",
  });
  return { deviceId: c.username, deviceToken: c.password };
}

export async function forgetSignIn() {
  if (!isNativeApp()) return;
  try {
    const { NativeBiometric } = await plugin();
    await NativeBiometric.deleteCredentials({ server: SERVER });
  } catch {
    // Nothing saved is the state we wanted anyway.
  }
}

// True once per launch of the app. The Face ID prompt opens by itself
// only then: after an inactivity timeout the phone may be in a pocket.
let coldStart = true;
export function consumeColdStart() {
  const was = coldStart;
  coldStart = false;
  return was;
}

// The device's id (not its token) kept in ordinary storage, so that
// signing out can tell the server which phone to revoke without first
// asking for Face ID. The id alone opens nothing.
const DEVICE_ID_KEY = "grtp_app_device_id";
export function rememberDeviceId(id) {
  try {
    if (id) localStorage.setItem(DEVICE_ID_KEY, id);
    else localStorage.removeItem(DEVICE_ID_KEY);
  } catch {
    // Storage refused: signing out still forgets the token on the phone.
  }
}
export function savedDeviceId() {
  try { return localStorage.getItem(DEVICE_ID_KEY); } catch { return null; }
}

// ── Notifications ──────────────────────────────────────────────────────
// iPhones through Apple, Android phones through Google's Firebase. The
// server decides who is notified and what it says (backend/push.js); the
// phone only asks permission, hands over its address, and opens the right
// screen when a notification is tapped.
const pushPlugin = () => import("@capacitor/push-notifications");
const pushSupported = () => isNativeApp() && (nativePlatform() === "ios" || nativePlatform() === "android");

let pushListening = false;
let pushStarted = false;
let tokenHandler = null;
let openHandler = null;
let pendingOpen = null; // tapped before anyone was signed in to receive it
let lastToken = null;   // Apple's address for this phone, once it has given one

async function listenForPush() {
  if (pushListening) return;
  pushListening = true;
  const { PushNotifications } = await pushPlugin();
  await PushNotifications.addListener("registration", (t) => {
    lastToken = t.value;
    if (tokenHandler) tokenHandler(t.value);
  });
  await PushNotifications.addListener("registrationError", (e) => console.warn("Notifications unavailable:", e?.error));
  await PushNotifications.addListener("pushNotificationActionPerformed", (a) => {
    const view = a?.notification?.data?.view || "dashboard";
    if (openHandler) openHandler(view);
    else pendingOpen = view;
  });
}

/** "granted", "denied", "prompt", or "unavailable" (web, or no plugin). */
export async function notificationPermission() {
  if (!pushSupported()) return "unavailable";
  try {
    const { PushNotifications } = await pushPlugin();
    const { receive } = await PushNotifications.checkPermissions();
    if (receive === "granted") return "granted";
    if (receive === "denied") return "denied";
    return "prompt";
  } catch {
    return "unavailable";
  }
}

/**
 * Starts notifications for this launch: asks permission if `ask` is set
 * and the person hasn't answered yet, then registers with Apple. The
 * address Apple returns goes to onToken. Returns the permission state.
 */
export async function startNotifications({ ask = false, onToken }) {
  if (!pushSupported()) return "unavailable";
  try {
    tokenHandler = onToken;
    await listenForPush();
    const { PushNotifications } = await pushPlugin();
    let state = await notificationPermission();
    if (state === "prompt" && ask) {
      const { receive } = await PushNotifications.requestPermissions();
      state = receive === "granted" ? "granted" : "denied";
    }
    if (state === "granted" && !pushStarted) {
      pushStarted = true;
      if (nativePlatform() === "android") {
        // Android files notifications under a channel the person can see
        // and switch off in Settings. Without one they land in a generic
        // "Miscellaneous" channel that doesn't pop up on screen. High
        // importance: a parent's message should be seen. The id matches
        // the server's (backend/push.js) and the manifest's default.
        await PushNotifications.createChannel({
          id: "grtp_updates",
          name: "Messages and stage updates",
          description: "When a parent messages you or a player you're named on moves stage",
          importance: 4,
        }).catch(() => {});
      }
      await PushNotifications.register();
    } else if (state === "granted" && lastToken) {
      // Already registered this launch, but perhaps for a phone record
      // that has since been signed out and replaced: send it again.
      onToken(lastToken);
    }
    return state;
  } catch {
    return "unavailable";
  }
}

/** Where to go when a notification is tapped. Delivers any tap that arrived before sign-in. */
export function onNotificationOpen(handler) {
  openHandler = handler;
  if (handler && pendingOpen) {
    const view = pendingOpen;
    pendingOpen = null;
    handler(view);
  }
  if (isNativeApp() && pushSupported()) listenForPush().catch(() => {});
}

// ── Android back button ────────────────────────────────────────────────
// The handler returns true if it went back a step itself. If it didn't
// (already on the dashboard, or signed out), the app goes to the
// background, which is what Android users expect of back on the first
// screen, rather than closing and needing Face/fingerprint unlock again.
// Listening at all replaces Capacitor's default (browser history, then
// close), which this app has no use for: its screens aren't history.
//
// Two handlers, asked in order: something open on top of the screen (the
// side menu) closes first, then the screen itself goes back.
const backHandlers = { overlay: null, screen: null };
let backListening = false;
export function onBackButton(handler, slot = "screen") {
  backHandlers[slot] = handler;
  if (backListening || !isNativeApp() || nativePlatform() !== "android") return;
  backListening = true;
  import("@capacitor/app")
    .then(({ App }) => App.addListener("backButton", () => {
      if (backHandlers.overlay && backHandlers.overlay()) return;
      if (backHandlers.screen && backHandlers.screen()) return;
      App.minimizeApp();
    }))
    .catch(() => { backListening = false; });
}

// Goes back one step the way Android's back button does (menu first, then
// the screen), but never puts the app away: on the dashboard it does
// nothing. Returns whether anything went back.
export function goBack() {
  if (backHandlers.overlay && backHandlers.overlay()) return true;
  if (backHandlers.screen && backHandlers.screen()) return true;
  return false;
}

// iPhone: a swipe in from the left edge goes back. The phone's own swipe
// can't be used, because the app's screens are not separate pages, so
// there is nothing for it to go back to. Android already has the system
// back gesture, which arrives as the back button above.
export function enableEdgeSwipeBack() {
  if (!isNativeApp() || nativePlatform() !== "ios") return () => {};
  const EDGE = 24, DISTANCE = 70, DRIFT = 50;
  let start = null;
  const onStart = (e) => {
    const t = e.touches[0];
    start = e.touches.length === 1 && t.clientX <= EDGE ? { x: t.clientX, y: t.clientY } : null;
  };
  const onEnd = (e) => {
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x, dy = Math.abs(t.clientY - start.y);
    start = null;
    if (dx >= DISTANCE && dy <= DRIFT) goBack();
  };
  document.addEventListener("touchstart", onStart, { passive: true });
  document.addEventListener("touchend", onEnd, { passive: true });
  return () => {
    document.removeEventListener("touchstart", onStart);
    document.removeEventListener("touchend", onEnd);
  };
}

// The phone app: pull down from the top of a screen to reload it. A
// browser already does this itself (reloading the whole page), so it is
// only added inside the app. The hint is drawn here, outside React, so it
// sits above every screen without each one having to include it.
export function enablePullToRefresh(onRefresh) {
  if (!isNativeApp()) return () => {};
  const THRESHOLD = 70;
  const hint = document.createElement("div");
  hint.setAttribute("aria-live", "polite");
  hint.style.cssText = "position:fixed;left:50%;top:calc(env(safe-area-inset-top, 0px) + 66px);" +
    "transform:translateX(-50%);z-index:4000;background:#1a4a2e;color:#fff;font:600 12.5px Inter,system-ui,sans-serif;" +
    "padding:7px 14px;border-radius:16px;box-shadow:0 2px 10px rgba(0,0,0,0.2);opacity:0;pointer-events:none;transition:opacity .15s";
  document.body.appendChild(hint);
  const show = (text) => { hint.textContent = text; hint.style.opacity = "1"; };
  const hide = () => { hint.style.opacity = "0"; };

  let startY = null, pulled = 0, refreshing = false;
  const atTop = () => (window.scrollY || document.documentElement.scrollTop || 0) <= 0;
  const onStart = (e) => {
    const target = e.target;
    // Not from inside a box being typed in or scrolled (a message, a list
    // in a dialog), where dragging down means something else.
    const inEditable = target && target.closest && target.closest("input, textarea, select, [contenteditable]");
    // Or inside a list that is itself scrolled down: pulling there scrolls
    // the list back up, which is what the person means.
    let inScrolled = false;
    for (let el = target; el && el !== document.body; el = el.parentElement) {
      if (el.scrollTop > 0) { inScrolled = true; break; }
    }
    startY = !refreshing && e.touches.length === 1 && atTop() && !inEditable && !inScrolled ? e.touches[0].clientY : null;
    pulled = 0;
  };
  const onMove = (e) => {
    if (startY === null) return;
    pulled = e.touches[0].clientY - startY;
    if (pulled <= 10 || !atTop()) { hide(); return; }
    show(pulled >= THRESHOLD ? "↑ Release to refresh" : "↓ Pull to refresh");
  };
  const onEnd = async () => {
    if (startY === null) return;
    startY = null;
    if (pulled < THRESHOLD || !atTop()) { hide(); return; }
    refreshing = true;
    show("Refreshing…");
    try { await onRefresh(); } catch { /* the screen shows its own errors */ }
    refreshing = false;
    hide();
  };
  document.addEventListener("touchstart", onStart, { passive: true });
  document.addEventListener("touchmove", onMove, { passive: true });
  document.addEventListener("touchend", onEnd, { passive: true });
  return () => {
    document.removeEventListener("touchstart", onStart);
    document.removeEventListener("touchmove", onMove);
    document.removeEventListener("touchend", onEnd);
    hint.remove();
  };
}

// Coming back to the app (or back to the browser tab). Inside the app the
// web view's own visibility event isn't reliable, so the app's resume
// event is used there.
export function onComeBack(handler) {
  if (isNativeApp()) {
    let listener = null, cancelled = false;
    import("@capacitor/app")
      .then(({ App }) => App.addListener("resume", handler))
      .then((l) => { if (cancelled) l.remove(); else listener = l; })
      .catch(() => {});
    return () => { cancelled = true; if (listener) listener.remove(); };
  }
  const onVisible = () => { if (document.visibilityState === "visible") handler(); };
  document.addEventListener("visibilitychange", onVisible);
  return () => document.removeEventListener("visibilitychange", onVisible);
}

// Whether the person said "Not now" to turning notifications on, so the
// offer isn't repeated on every screen. Settings > Notifications on the
// phone is always there if they change their mind.
const PUSH_DECLINED_KEY = "grtp_push_offer_declined";
export function notificationsOfferDeclined() {
  try { return localStorage.getItem(PUSH_DECLINED_KEY) === "1"; } catch { return false; }
}
export function declineNotificationsOffer() {
  try { localStorage.setItem(PUSH_DECLINED_KEY, "1"); } catch { /* shown again next time: harmless */ }
}

// ── Saving a file (PDFs, CSVs, exports) ────────────────────────────────
//
// In a browser, a download link saves the file. Inside the phone app it
// does nothing at all: the app's web view ignores the download attribute,
// so the app said "PDF downloaded" and nothing was saved. On a phone the
// file is written to the app's private cache instead and handed to the
// share sheet, where it can be saved to Files, emailed, AirDropped or
// printed. The cached copy is deleted as soon as the sheet closes: these
// are health records, and a copy left behind in the app is one nobody
// knows is there.
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.readAsDataURL(blob);
  });
}

/**
 * Saves a file the way this device can: a download in a browser, the
 * share sheet in the phone app. Returns "downloaded", "shared" or
 * "cancelled" (the person closed the share sheet without choosing).
 */
export async function saveFile(blob, filename) {
  const safeName = String(filename || "download").replace(/[^A-Za-z0-9._-]+/g, "-");
  if (!isNativeApp()) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = safeName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoked on the next tick: some browsers start the download after
    // click() returns, and an already-revoked URL downloads nothing.
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    return "downloaded";
  }

  const { Filesystem, Directory } = await import("@capacitor/filesystem");
  const { Share } = await import("@capacitor/share");
  // A folder per export keeps two exports apart without changing the
  // file's own name, which is what the coach sees in Files or an email.
  const folder = `exports/${Date.now()}`;
  const path = `${folder}/${safeName}`;
  const written = await Filesystem.writeFile({
    path, data: await blobToBase64(blob), directory: Directory.Cache, recursive: true,
  });
  try {
    await Share.share({ title: safeName, files: [written.uri] });
    return "shared";
  } catch (e) {
    // Closing the sheet without picking anything rejects; that is a
    // choice, not a failure.
    if (/cancel/i.test(String(e && (e.message || e)))) return "cancelled";
    throw e;
  } finally {
    Filesystem.rmdir({ path: folder, directory: Directory.Cache, recursive: true }).catch(() => {});
  }
}

/** On launch: removes any export left behind if the app was closed mid-share. */
export async function clearExportCache() {
  if (!isNativeApp()) return;
  try {
    const { Filesystem, Directory } = await import("@capacitor/filesystem");
    await Filesystem.rmdir({ path: "exports", directory: Directory.Cache, recursive: true });
  } catch {
    // Nothing there is the normal case.
  }
}

/** A short, recognisable name for the admin's list of phones. */
export async function deviceLabel() {
  try {
    const { Device } = await import("@capacitor/device");
    const info = await Device.getInfo();
    const name = [info.manufacturer, info.model].filter(Boolean).join(" ");
    return name.slice(0, 60) || null;
  } catch {
    return null;
  }
}
